import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom'

import { useHostResolver } from '@/hooks/useHostResolver'
import { LoadingScreen } from '@/components/auth/LoadingScreen'

/* ── Public-facing lazy imports ────────────────────────── */
const publicListingsImport = () => import('@/modules/listings/pages/PublicListingsPage').then(m => ({ default: m.PublicListingsPage }))
const PublicListingsPage      = lazy(publicListingsImport)
const SSRListingPage          = lazy(() => import('@/modules/listings/pages/SSRListingPage').then(m => ({ default: m.SSRListingPage })))
const LeadPortalPage          = lazy(() => import('@/pages/public/LeadPortalPage').then(m => ({ default: m.LeadPortalPage })))
const LeadAuthPage            = lazy(() => import('@/pages/public/LeadAuthPage').then(m => ({ default: m.LeadAuthPage })))
const BookingPage             = lazy(() => import('@/modules/bookings/pages/BookingPage').then(m => ({ default: m.BookingPage })))
const BookingSuccessPage      = lazy(() => import('@/modules/bookings/pages/BookingSuccessPage').then(m => ({ default: m.BookingSuccessPage })))
const ConfirmBookingsPage     = lazy(() => import('@/modules/bookings/pages/ConfirmBookingsPage').then(m => ({ default: m.ConfirmBookingsPage })))
const PublicCheckInPage       = lazy(() => import('@/pages/checkin/PublicCheckInPage').then(m => ({ default: m.PublicCheckInPage })))

/* ── Lazy-load the authenticated app shell (includes Supabase) ── */
const LazyAuthApp = lazy(() => import('@/AuthApp'))

const PageSpinner = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
  </div>
)

/* ── Lead portal route — reads :token (+ optional :action) ── */
function LeadPortalPageRoute() {
  const { token, action } = useParams<{ token: string; action?: string }>()
  if (!token) return null
  return <LeadPortalPage token={token} action={action} />
}

/* ── Public domain shell — no AuthProvider, no Supabase client ── */
function PublicDomainShell() {
  const { loading, resolvedAccountId, resolvedPurpose } = useHostResolver()

  useEffect(() => { void publicListingsImport() }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (resolvedAccountId && resolvedPurpose === 'listings') {
    return (
      <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/l/:token/:action"   element={<LeadPortalPageRoute />} />
        <Route path="/l/:token"          element={<LeadPortalPageRoute />} />
        <Route path="/confirm/:token"    element={<ConfirmBookingsPage />} />
        <Route path="/book/:id/success"  element={<BookingSuccessPage />} />
        <Route path="/book/:id"          element={<BookingPage />} />
        <Route path="/checkin/:id"       element={<PublicCheckInPage />} />
        <Route path="/listing/:listingId" element={<SSRListingPage accountId={resolvedAccountId} />} />
        <Route path="*"                  element={<PublicListingsPage accountId={resolvedAccountId} />} />
      </Routes>
      </Suspense>
    )
  }

  if (resolvedAccountId && resolvedPurpose === 'bookings') {
    return (
      <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/l/:token/:action"  element={<LeadPortalPageRoute />} />
        <Route path="/l/:token"          element={<LeadPortalPageRoute />} />
        <Route path="/confirm/:token"    element={<ConfirmBookingsPage />} />
        <Route path="/book/:id/success"  element={<BookingSuccessPage />} />
        <Route path="/book/:id"          element={<BookingPage />} />
        <Route path="/checkin/:id"       element={<PublicCheckInPage />} />
        <Route path="*"                  element={<LeadAuthPage accountId={resolvedAccountId} />} />
      </Routes>
      </Suspense>
    )
  }

  // Not a listings/bookings domain (e.g. inspections) — needs full auth app
  return (
    <Suspense fallback={<PageSpinner />}>
      <LazyAuthApp />
    </Suspense>
  )
}

/* ── Root ───────────────────────────────────────────────── */
const PLATFORM_HOSTS = new Set(['localhost', '127.0.0.1', 'app.agntx.com.au', 'agntx.com.au'])
const isPublicDomain = typeof window !== 'undefined' && !PLATFORM_HOSTS.has(window.location.hostname) && !window.location.hostname.includes('azurestaticapps.net')

export default function App() {
  return (
    <BrowserRouter>
      {isPublicDomain ? <PublicDomainShell /> : <Suspense fallback={<PageSpinner />}><LazyAuthApp /></Suspense>}
    </BrowserRouter>
  )
}

export { LoadingScreen }
